import axios from 'axios';
import {
  AuthResponse,
  LoginCredentials,
  LoginResponse,
  RegisterCredentials,
  RegisterResponse,
  UpdateProfilePayload,
  User,
  Slider,
  Article,
  ArticleCategory,
  Podcast,
  VideoPodcast,
  Course,
  Video,
  VideoStreamInfo,
  Audio,
  AudioStreamInfo,
  Workshop,
  Comment,
  UserMessage,
  Lead,
  PracticeUserState,
  ContentCategoryPublic,
  AssessmentLanding,
  AssessmentForUser,
  AssessmentResult,
  AssessmentAttemptSummary,
  AssessmentHistoryResult,
} from '../types';
import type { ContentCategoryTreeNode } from '../utils/courseDiscoveryHome';
import { ApiError } from '../contexts/ErrorContext';
import type {
  PortalAssignmentDetail,
  PortalDashboard,
  PortalMessage,
  PortalMessageAttachment,
  PortalNotification,
  PortalQuestion,
  PortalSummary,
  PortalTicket,
  SubmissionFile,
} from '../types/portal';
import type {
  BusinessCaseResult,
  BusinessFormPayload,
  BusinessLanding,
  BusinessProfile,
} from '../types/business';
import type {
  RecruitmentPublicSettings,
  RecruitmentSubmissionPayload,
} from '../types/recruitment';

const DEFAULT_LOCAL_API = 'http://localhost:3000/api';
const DEFAULT_SERVER_API = 'https://api.manehaghighi.com/api';

const normalizeUrl = (url: string) => url.replace(/\/$/, '');

const isLocalHost = (hostname: string) =>
  hostname === 'localhost' || hostname === '127.0.0.1';

const getApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_API_URL?.trim();

  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const isHttps = window.location.protocol === 'https:';

    // If on localhost, use local API
    if (isLocalHost(hostname)) {
      if (envUrl && (envUrl.includes('localhost') || envUrl.includes('127.0.0.1'))) {
        return normalizeUrl(envUrl);
      }
      return DEFAULT_LOCAL_API;
    }

    // Production: ترجیحاً همان دامنه (/api) تا nginx همان سایت پراکسی کند و ERR_FAILED ندهد
    if (envUrl) {
      const url = normalizeUrl(envUrl);
      return url.replace(/^http:\/\//, 'https://');
    }
    return '/api';
  }

  if (envUrl) {
    return normalizeUrl(envUrl);
  }

  return DEFAULT_SERVER_API;
};

export const API_BASE_URL = getApiBaseUrl();
export const API_ORIGIN =
  API_BASE_URL === '/api' || API_BASE_URL.startsWith('/')
    ? (typeof window !== 'undefined' ? window.location.origin : '')
    : API_BASE_URL.endsWith('/api')
      ? API_BASE_URL.slice(0, -4)
      : API_BASE_URL;

// Global error handler that can be set by React components
let globalErrorHandler: ((error: ApiError) => void) | null = null;

export const setGlobalErrorHandler = (handler: (error: ApiError) => void) => {
  globalErrorHandler = handler;
};

export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  // List of public endpoints that don't require authentication
  const publicEndpoints = [
    '/auth/login',
    '/auth/register',
    '/auth/send-otp',
    '/auth/verify-otp',
  ];
  
  // Check if the current request is to a public endpoint
  const isPublicEndpoint = publicEndpoints.some(endpoint => 
    config.url?.includes(endpoint)
  );
  
  // Only add token if it's not a public endpoint
  if (!isPublicEndpoint) {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// Response interceptor to handle auth errors and 304 status
api.interceptors.response.use(
  (response) => {
    // Handle 304 Not Modified - ensure response.data exists
    if (response.status === 304 && !response.data) {
      // For 304, we should use cached data if available, or return empty array/object
      // This depends on the endpoint, but we'll let the service handle it
      response.data = response.data || null;
    }
    return response;
  },
  (error) => {
    /**
     * فرم ورود و درخواست سفیری خطاهای خود را در همان فرم نشان می‌دهند؛ اگر
     * toast سراسری هم بالا بیاید کاربر یک پیام را دو بار می‌بیند، آن هم یک‌بار
     * همراه با «POST /api/auth/login • کد وضعیت: 400» که برای او معنایی ندارد.
     */
    const selfHandledPaths = ['auth/login', 'auth/register', 'auth/send-otp', 'auth/verify-otp', 'ambassadors/me/application', '/vip-surveys/', '/assessments/'];
    const isSelfHandled = selfHandledPaths.some((path) =>
      error.config?.url?.includes(path),
    );

    // Handle API errors globally
    if (error.response?.data && !isSelfHandled) {
      const rawMessage = error.response.data.message;
      const message =
        typeof rawMessage === 'string' && rawMessage.trim()
          ? rawMessage.trim()
          : Array.isArray(rawMessage) && rawMessage.length > 0 && typeof rawMessage[0] === 'string'
            ? String(rawMessage[0]).trim()
            : 'خطای نامشخص سرور';
      const apiError: ApiError = {
        message,
        statusCode: error.response.data.statusCode,
        path: error.response.data.path,
        method: error.response.data.method,
        timestamp: error.response.data.timestamp,
        error: error.response.data.error,
        stack: error.response.data.stack,
      };

      // Call global error handler if set
      if (globalErrorHandler) {
        globalErrorHandler(apiError);
      }
    }

    // فقط برای ۴۰۱هایی که مربوط به درخواست لاگین/ثبت‌نام نیستند redirect و پاک‌کردن توکن
    // برای POST /auth/login و مشابه، ارور را دست‌نخورده به catch می‌دهیم تا پیام بک‌اند نمایش داده شود
    const isAuthLoginOrRegister =
      error.config?.url?.includes('auth/login') || error.config?.url?.includes('auth/register');
    if (error.response?.status === 401 && !isAuthLoginOrRegister) {
      const data = error.response?.data;
      const message = data?.message;
      const text = typeof message === 'string' && message.trim() ? message.trim() : 'کاربر وجود ندارد';
      try {
        sessionStorage.setItem('login_401_message', text);
      } catch (_) {}
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (window.location.pathname !== '/login') {
        const next = window.location.pathname.startsWith('/v/') || window.location.pathname.startsWith('/a/')
          ? `?next=${encodeURIComponent(window.location.pathname + window.location.search)}`
          : '';
        window.location.replace(`/login${next}`);
      }
    }
    return Promise.reject(error);
  }
);

export const authService = {
  login: async (credentials: LoginCredentials): Promise<LoginResponse> => {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  },

  register: async (credentials: RegisterCredentials): Promise<RegisterResponse> => {
    const response = await api.post('/auth/register', credentials);
    return response.data;
  },

  sendOtp: async (phone: string, channel: 'sms' | 'bale' = 'sms'): Promise<{ message: string }> => {
    const response = await api.post('/auth/send-otp', { phone, channel });
    return response.data;
  },

  verifyOtp: async (phone: string, otp: string): Promise<AuthResponse> => {
    const response = await api.post('/auth/verify-otp', { phone, otp });
    return response.data;
  },

  getProfile: async (): Promise<User> => {
    const response = await api.get('/auth/profile');
    return response.data;
  },

  updateProfile: async (payload: UpdateProfilePayload): Promise<AuthResponse> => {
    const response = await api.patch('/auth/profile', payload);
    return response.data;
  },

  changePassword: async (data: { currentPassword?: string; newPassword: string }): Promise<{ message: string }> => {
    const response = await api.patch('/auth/password', {
      currentPassword: data.currentPassword,
      newPassword: data.newPassword,
    });
    return response.data;
  },
};

export const slidersService = {
  getActive: async (): Promise<Slider[]> => {
    const response = await api.get('/sliders/active');
    return response.data;
  },
};

export interface ArticleListParams {
  page?: number;
  limit?: number;
  /** slug دسته */
  category?: string;
  categoryId?: string;
  tag?: string;
  search?: string;
  sort?: 'newest' | 'popular';
  hasVideo?: boolean;
  excludeFeatured?: boolean;
}

export interface ArticleListResponse {
  data: Article[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

export const articlesService = {
  getPublished: async (params?: ArticleListParams): Promise<ArticleListResponse> => {
    const response = await api.get('/articles/published', { params });
    const body = response.data || {};
    return {
      data: Array.isArray(body.data) ? body.data : [],
      meta: body.meta || { total: 0, page: 1, limit: 10, totalPages: 0 },
    };
  },

  getFeatured: async (limit = 3): Promise<Article[]> => {
    const response = await api.get('/articles/featured', { params: { limit } });
    return Array.isArray(response.data) ? response.data : [];
  },

  getBySlug: async (slug: string, preview?: string | null): Promise<Article> => {
    const response = await api.get(`/articles/slug/${encodeURIComponent(slug)}`, {
      params: preview ? { preview } : undefined,
    });
    return response.data;
  },

  getRelated: async (id: string, limit = 3): Promise<Article[]> => {
    const response = await api.get(`/articles/${id}/related`, { params: { limit } });
    return Array.isArray(response.data) ? response.data : [];
  },

  getCategories: async (): Promise<ArticleCategory[]> => {
    const response = await api.get('/articles/categories/all');
    return Array.isArray(response.data) ? response.data : [];
  },

  /** متن‌ها و لینک‌های ثابت هدر، فوتر و صفحه‌های وبلاگ (از پنل قابل ویرایش) */
  getSettings: async (): Promise<any> => {
    const response = await api.get('/articles/settings');
    return response.data || {};
  },

  sendFeedback: async (id: string, helpful: boolean): Promise<{ helpfulYes: number; helpfulNo: number }> => {
    const response = await api.post(`/articles/${id}/feedback`, { helpful });
    return response.data;
  },
};

export const podcastsService = {
  getPublished: async (): Promise<Podcast[]> => {
    const response = await api.get('/podcasts/published');
    return response.data;
  },

  getById: async (id: string): Promise<Podcast> => {
    const response = await api.get(`/podcasts/${id}`);
    return response.data;
  },
};

export const commentsService = {
  getArticleComments: async (articleId: string): Promise<Comment[]> => {
    const response = await api.get(`/comments/articles/${articleId}`);
    return response.data;
  },
  createArticleComment: async (
    articleId: string,
    data: { authorName: string; authorPhone?: string; content: string; parentId?: string; rating?: number },
  ): Promise<Comment> => {
    const response = await api.post(`/comments/articles/${articleId}`, data);
    return response.data;
  },

  getPodcastComments: async (podcastId: string): Promise<Comment[]> => {
    const response = await api.get(`/comments/podcasts/${podcastId}`);
    return response.data;
  },
  createPodcastComment: async (
    podcastId: string,
    data: { authorName: string; authorPhone?: string; content: string; parentId?: string; rating?: number },
  ): Promise<Comment> => {
    const response = await api.post(`/comments/podcasts/${podcastId}`, data);
    return response.data;
  },

  getCourseComments: async (courseId: string): Promise<Comment[]> => {
    const response = await api.get(`/comments/courses/${courseId}`);
    return response.data;
  },
  createCourseComment: async (
    courseId: string,
    data: { authorName: string; authorPhone?: string; content: string; parentId?: string; rating?: number },
  ): Promise<Comment> => {
    const response = await api.post(`/comments/courses/${courseId}`, data);
    return response.data;
  },
};

export const videoPodcastsService = {
  getPublished: async (): Promise<VideoPodcast[]> => {
    const response = await api.get('/video-podcasts/published');
    return response.data;
  },

  getById: async (id: string): Promise<VideoPodcast> => {
    const response = await api.get(`/video-podcasts/${id}`);
    return response.data;
  },
};

export const categoriesService = {
  /** دسته‌های محتوا؛ برای دوره از type=COURSE استفاده کنید */
  list: async (type?: 'COURSE' | 'PODCAST'): Promise<ContentCategoryPublic[]> => {
    const response = await api.get('/categories', { params: type ? { type } : undefined });
    return response.data;
  },

  /** درخت سلسله‌مراتبی دسته‌ها (ریشه → فرزندان) */
  tree: async (type: 'COURSE' | 'PODCAST'): Promise<ContentCategoryTreeNode[]> => {
    const response = await api.get('/categories/tree', { params: { type } });
    return Array.isArray(response.data) ? response.data : [];
  },
};

export const coursesService = {
  getPublished: async (): Promise<Course[]> => {
    const response = await api.get('/courses/published');
    return response.data;
  },

  getHomepageCarousels: async (): Promise<{
    newest: Course[];
    popular: Course[];
    bestselling: Course[];
    free: Course[];
  }> => {
    const response = await api.get('/courses/homepage/carousels');
    return response.data;
  },

  getForHomepage: async (): Promise<Course[]> => {
    const response = await api.get('/courses/homepage');
    return response.data;
  },

  /** تعداد دوره‌های منتشرشده در کتابخانه (بدون دریافت لیست کامل) */
  getPublishedCount: async (): Promise<number> => {
    const response = await api.get<{ count: number }>('/courses/published/count');
    return typeof response.data?.count === 'number' ? response.data.count : 0;
  },

  getById: async (id: string): Promise<Course> => {
    const response = await api.get(`/courses/${id}`);
    return response.data;
  },

  enroll: async (courseId: string): Promise<void> => {
    await api.post(`/courses/${courseId}/enroll`);
  },

  getMyCourses: async (): Promise<Course[]> => {
    const response = await api.get('/courses/my-courses');
    return response.data;
  },

  getPracticeState: async (courseId: string): Promise<PracticeUserState> => {
    const response = await api.get(`/courses/${courseId}/practice/state`);
    return response.data;
  },

  setPracticeTaskDone: async (courseId: string, taskId: string, done: boolean) => {
    const response = await api.put(`/courses/${courseId}/practice/tasks/${taskId}`, { done });
    return response.data;
  },

  putPracticeFeedback: async (
    courseId: string,
    sessionId: string,
    body: { rating: number; comment?: string; submitted?: boolean },
  ) => {
    const response = await api.put(`/courses/${courseId}/practice/sessions/${sessionId}/feedback`, body);
    return response.data;
  },
};

export const videosService = {
  getMyVideos: async (): Promise<Video[]> => {
    const response = await api.get('/videos/my-videos');
    return response.data;
  },

  getStreamInfo: async (videoId: string) => {
    const response = await api.get(`/videos/${videoId}/stream`);
    return response.data;
  },

  getVideoStreamUrl: async (videoId: string): Promise<VideoStreamInfo> => {
    const response = await api.get(`/videos/${videoId}/stream-url`);
    return response.data;
  },

  refreshPlaybackToken: async (videoId: string): Promise<Pick<VideoStreamInfo, 'videoId' | 'streamUrl' | 'playbackTtlSeconds'>> => {
    const response = await api.post(`/videos/${videoId}/refresh-playback`);
    return response.data;
  },
};

export const audiosService = {
  getMyAudios: async (): Promise<Audio[]> => {
    const response = await api.get('/audios/my-audios');
    return response.data;
  },

  getAudioStreamUrl: async (audioId: string): Promise<AudioStreamInfo> => {
    const response = await api.get(`/audios/${audioId}/stream-url`);
    return response.data;
  },
};

export const paymentsService = {
  /** ثبت‌نام عمومی کارگاه شفای زخم از صفحهٔ لندینگ؛ بدون نیاز به ورود کاربر */
  registerShefayeZakhmWorkshop: async (data: { customerName: string; customerMobile: string }) => {
    const response = await api.post('/payments/public/workshops/shefaye-zakhm/register', data);
    return response.data as { paymentUrl: string };
  },

  createOrGetCartOrder: async (courseId: string) => {
    const response = await api.post(`/payments/course/${courseId}/cart-order`);
    return response.data as { id: string; courseId: string; status: string; transactionId?: string | null };
  },

  markOrderPendingPayment: async (invoiceId: string) => {
    const response = await api.patch(`/payments/invoices/${invoiceId}/mark-pending-payment`);
    return response.data;
  },

  initiateCoursePayment: async (
    courseId: string,
    opts?: { couponCode?: string; couponCartCourseIds?: string[] },
  ) => {
    const response = await api.post(`/payments/course/${courseId}`, opts ?? {});
    return response.data;
  },

  previewCartCoupon: async (code: string, courseIds: string[]) => {
    const response = await api.post('/coupons/preview-cart', { code, courseIds });
    return response.data as {
      kind: 'none' | 'partial' | 'applied';
      rejectionReason?: string;
      totalIntrinsicToman: number;
      totalDiscountToman: number;
      totalFinalPayableToman: number;
      lines: Array<{
        courseId: string;
        title: string;
        ok: boolean;
        rejectionReason?: string;
        intrinsicEffectiveToman: number;
        discountToman: number;
        finalPayableToman: number;
      }>;
    };
  },

  previewCourseCoupon: async (courseId: string, code: string) => {
    const response = await api.post('/coupons/preview', { courseId, code });
    return response.data as {
      kind?: 'none' | 'applied';
      rejectionReason?: string;
      intrinsicEffectiveToman?: number;
      finalPayableToman?: number;
      discountToman?: number;
    };
  },

  getWalletBalance: async (): Promise<{ balance: number }> => {
    const response = await api.get('/payments/wallet/balance');
    return response.data;
  },

  /**
   * amountRial: مبلغ به ریال (درگاه پی‌پینگ). courseIds در صورت ارسال، پس از
   * پرداخت موفق و شارژ کیف پول، به‌صورت خودکار از کیف پول خریداری می‌شوند.
   */
  chargeWallet: async (
    amountRial: number,
    description?: string,
    opts?: { courseIds?: string[]; couponCode?: string },
  ): Promise<{ success: boolean; paymentUrl: string; refId: string; invoice: any; transaction: any }> => {
    const response = await api.post('/payments/wallet/charge', {
      amount: amountRial,
      description,
      courseIds: opts?.courseIds,
      couponCode: opts?.couponCode,
    });
    return response.data;
  },

  uploadTopupReceipt: async (file: File): Promise<{ key: string; url: string; filename: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/payments/wallet/topup-receipt', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  createWalletTopupRequest: async (data: {
    amount: number;
    depositMethod?: 'CARD' | 'SHEBA' | 'ACCOUNT';
    receiptReference?: string;
    receiptImageUrl?: string;
    note?: string;
  }) => {
    const response = await api.post('/payments/wallet/topup-requests', data);
    return response.data;
  },

  getMyWalletTopupRequests: async () => {
    const response = await api.get('/payments/wallet/topup-requests');
    return response.data;
  },

  getTransactionById: async (transactionId: string) => {
    const response = await api.get(`/payments/transactions/${transactionId}`);
    return response.data;
  },

  getMyInvoices: async (limit = 50) => {
    const response = await api.get(`/payments/invoices?limit=${limit}`);
    return response.data;
  },

  getInvoiceById: async (invoiceId: string) => {
    const response = await api.get(`/payments/invoices/${invoiceId}`);
    return response.data;
  },

  getPaymentLinkByCode: async (linkCode: string) => {
    const response = await api.get(`/api/payments/pay/${linkCode}`);
    return response.data;
  },
};

export const workshopsService = {
  getActive: async (): Promise<Workshop[]> => {
    try {
      const response = await api.get('/workshops/active');
      // Handle 304 Not Modified and ensure we return an array
      if (response.status === 304 && !response.data) {
        return [];
      }
      // Ensure response.data is an array
      return Array.isArray(response.data) ? response.data : (response.data?.data || []);
    } catch (error: any) {
      // If it's a 304 error, return empty array instead of throwing
      if (error.response?.status === 304) {
        return [];
      }
      throw error;
    }
  },

  getMyWorkshops: async (): Promise<Workshop[]> => {
    const response = await api.get('/workshops/my-workshops');
    return response.data;
  },

  getById: async (id: string): Promise<Workshop> => {
    const response = await api.get(`/workshops/${id}`);
    return response.data;
  },

  preRegister: async (workshopId: string, participantData: {
    customerPhone: string;
    customerName: string;
  }): Promise<void> => {
    await api.post(`/workshops/${workshopId}/participants`, participantData);
  },

  getMyWorkshopParticipants: async (): Promise<any[]> => {
    const response = await api.get('/workshops/my-workshops');
    return response.data;
  },

  getParticipantPayments: async (participantId: string): Promise<any> => {
    const response = await api.get(`/workshops/participants/${participantId}/payments`);
    return response.data;
  },

  completeWorkshopPayment: async (participantId: string, paymentData: {
    amount: number;
    paymentMethod?: string;
  }): Promise<any> => {
    const response = await api.post(`/workshops/participants/${participantId}/complete-payment`, paymentData);
    return response.data;
  },
};

export const messagesService = {
  getMyMessages: async (): Promise<UserMessage[]> => {
    const response = await api.get('/messages/my');
    return response.data;
  },
  markAsRead: async (userMessageId: string): Promise<UserMessage> => {
    const response = await api.patch(`/messages/my/${userMessageId}/read`);
    return response.data;
  },
};

export const leadsService = {
  create: async (data: {
    fullName: string;
    phone: string;
    interest?: string;
    source?: string;
    note?: string;
    isBusinessOwner?: boolean;
    staffCount?: string;
    businessField?: string;
    jobType?: string;
  }): Promise<Lead> => {
    const response = await api.post('/leads', data);
    return response.data;
  },
};

export const assessmentsService = {
  /** بدون نیاز به ورود — برای صفحهٔ لندینگ مهمان */
  getLanding: async (slug?: string, round?: string): Promise<AssessmentLanding> => {
    const response = await api.get('/assessments/landing', { params: { slug, round } });
    return response.data;
  },
  /**
   * `workshopId`: رشتهٔ خالی یعنی «بدون کارگاه» (صریح) و undefined یعنی «سرور تصمیم بگیرد»؛
   * `workshopSlug` همان پارامتر `w` لینک پیامکی است.
   */
  getForUser: async (
    slug?: string,
    workshopId?: string,
    invite?: string,
    workshopSlug?: string,
    round?: string,
    attempt?: string,
  ): Promise<AssessmentForUser> => {
    const response = await api.get('/assessments/active', {
      params: {
        ...(slug ? { slug } : {}),
        ...(workshopId !== undefined ? { workshopId } : {}),
        ...(invite ? { invite } : {}),
        ...(workshopSlug ? { w: workshopSlug } : {}),
        ...(round ? { round } : {}),
        ...(attempt ? { attempt } : {}),
      },
    });
    return response.data;
  },
  /** `workshopId`: رشتهٔ خالی = بدون کارگاه، undefined = تنها کارگاه فعال (تصمیم سرور) */
  startAttempt: async (
    assessmentId: string,
    workshopId?: string,
    invite?: string,
    round?: string,
  ): Promise<{ id: string; workshopId: string | null }> => {
    const response = await api.post(`/assessments/${assessmentId}/attempts`, { workshopId, invite, round });
    return response.data;
  },
  saveAnswers: async (
    attemptId: string,
    answers: Array<{ questionId: string; value: number }>,
  ): Promise<{ saved: number }> => {
    const response = await api.patch(`/assessments/attempts/${attemptId}/answers`, { answers });
    return response.data;
  },
  setWorkshop: async (attemptId: string, workshopId: string): Promise<void> => {
    await api.patch(`/assessments/attempts/${attemptId}/workshop`, { workshopId });
  },
  submit: async (attemptId: string): Promise<AssessmentResult> => {
    const response = await api.post(`/assessments/attempts/${attemptId}/submit`);
    return response.data;
  },
  getAttemptResult: async (attemptId: string): Promise<AssessmentResult> => {
    const response = await api.get(`/assessments/attempts/${attemptId}/result`);
    return response.data;
  },
  /**
   * آخرین نتیجهٔ تکمیل‌شده؛ `null` یعنی کاربر هنوز پرسشنامه را کامل نکرده.
   * بدون `slug` فقط نتیجهٔ پرسشنامه‌های مسیر رشد برمی‌گردد (برای داشبورد)؛
   * صفحهٔ پرسشنامه همیشه slug خودش را می‌دهد تا نتیجهٔ پرسشنامهٔ دیگری را نشان ندهد.
   */
  getMyResult: async (slug?: string, round?: string): Promise<AssessmentResult | null> => {
    const response = await api.get('/assessments/me/result', {
      params: { slug, round },
    });
    return response.data || null;
  },
  getMyAttempts: async (): Promise<AssessmentAttemptSummary[]> => {
    const response = await api.get('/assessments/me/attempts');
    return response.data;
  },
  getMyHistory: async (): Promise<AssessmentHistoryResult[]> => {
    const response = await api.get('/assessments/me/history');
    return response.data;
  },
};

// ─── فرم ارزیابی کسب‌وکار (6C) ────────────────────────────────────────────────

export const businessService = {
  getLanding: async (slug?: string): Promise<BusinessLanding> => {
    const response = await api.get('/business/landing', { params: slug ? { slug } : undefined });
    return response.data;
  },
  getForm: async (slug?: string, invite?: string): Promise<BusinessFormPayload> => {
    const response = await api.get('/business/form', {
      params: { ...(slug ? { slug } : {}), ...(invite ? { invite } : {}) },
    });
    return response.data;
  },
  startCase: async (formId: string, invite?: string): Promise<{ id: string }> => {
    const response = await api.post(`/business/${formId}/cases`, { invite });
    return response.data;
  },
  saveProfile: async (caseId: string, profile: Partial<BusinessProfile>): Promise<void> => {
    await api.patch(`/business/cases/${caseId}/profile`, profile);
  },
  saveAnswers: async (
    caseId: string,
    answers: Array<{ statementId: string; score: number; evidence?: string }>,
  ): Promise<{ saved: number }> => {
    const response = await api.patch(`/business/cases/${caseId}/answers`, { answers });
    return response.data;
  },
  saveMetrics: async (
    caseId: string,
    metrics: Array<{
      metricId: string;
      current?: string;
      target?: string;
      source?: string;
      notMeasured?: boolean;
    }>,
  ): Promise<{ saved: number }> => {
    const response = await api.patch(`/business/cases/${caseId}/metrics`, { metrics });
    return response.data;
  },
  saveFlags: async (
    caseId: string,
    flags: Array<{ flagId: string; value: boolean; note?: string }>,
  ): Promise<{ saved: number }> => {
    const response = await api.patch(`/business/cases/${caseId}/flags`, { flags });
    return response.data;
  },
  submit: async (caseId: string): Promise<BusinessCaseResult> => {
    const response = await api.post(`/business/cases/${caseId}/submit`);
    return response.data;
  },
  /** آخرین پروندهٔ ثبت‌شده؛ `null` یعنی هنوز فرمی پر نکرده */
  getMyCase: async (): Promise<BusinessCaseResult | null> => {
    const response = await api.get('/business/me/case');
    return response.data || null;
  },
};

// ─── پرسشنامه استخدام ─────────────────────────────────────────────────────────

export const recruitmentService = {
  getSettings: async (): Promise<RecruitmentPublicSettings> => {
    const response = await api.get('/recruitment/settings');
    return response.data;
  },
  submit: async (payload: RecruitmentSubmissionPayload): Promise<{ id: string }> => {
    const response = await api.post('/recruitment/submissions', payload);
    return response.data;
  },
};

// ─── پرتال رشد ۶C ─────────────────────────────────────────────────────────────

export const portalService = {
  /** خلاصه برای داشبورد سایت؛ `null` یعنی عضو دوره نیست */
  getSummary: async (): Promise<PortalSummary | null> => {
    const response = await api.get('/portal/me/summary');
    return response.data || null;
  },
  getDashboard: async (): Promise<PortalDashboard> => {
    const response = await api.get('/portal/me');
    return response.data;
  },
  getTask: async (assignmentId: string): Promise<PortalAssignmentDetail> => {
    const response = await api.get('/portal/tasks/' + assignmentId);
    return response.data;
  },
  submitTask: async (
    assignmentId: string,
    values: Array<{
      fieldId: string;
      textValue?: string;
      numberValue?: number;
      dateValue?: string;
      jsonValue?: unknown;
      fileIds?: string[];
    }>,
  ): Promise<PortalAssignmentDetail> => {
    const response = await api.post('/portal/tasks/' + assignmentId + '/submit', { values });
    return response.data;
  },
  uploadFile: async (
    assignmentId: string,
    fieldId: string,
    file: File,
    onProgress?: (percent: number) => void,
  ): Promise<SubmissionFile> => {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post(
      '/portal/tasks/' + assignmentId + '/files/' + fieldId,
      form,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (event) => {
          if (onProgress && event.total) {
            onProgress(Math.round((event.loaded / event.total) * 100));
          }
        },
      },
    );
    return response.data;
  },
  removeFile: async (fileId: string): Promise<void> => {
    await api.delete('/portal/files/' + fileId);
  },
  /** دانلود از مسیر احرازهویت‌شده؛ فایل هیچ‌وقت مستقیم سرو نمی‌شود */
  downloadFile: async (fileId: string, filename: string): Promise<void> => {
    const response = await api.get('/portal/files/' + fileId + '/download', {
      responseType: 'blob',
    });
    triggerDownload(response.data, filename);
  },
  downloadMaterial: async (materialId: string, filename: string): Promise<void> => {
    const response = await api.get('/portal/materials/' + materialId + '/download', {
      responseType: 'blob',
    });
    triggerDownload(response.data, filename);
  },
  getQuestions: async (): Promise<PortalQuestion[]> => {
    const response = await api.get('/portal/questions');
    return response.data;
  },
  askQuestion: async (subject: string, body: string): Promise<PortalQuestion> => {
    const response = await api.post('/portal/questions', { subject, body });
    return response.data;
  },
  getTickets: async (): Promise<PortalTicket[]> => {
    const response = await api.get('/portal/tickets');
    return response.data;
  },
  openTicket: async (subject: string, body: string): Promise<PortalTicket> => {
    const response = await api.post('/portal/tickets', { subject, body });
    return response.data;
  },
  getNotifications: async (unread = false): Promise<PortalNotification[]> => {
    const response = await api.get('/portal/notifications', {
      params: unread ? { unread: 'true' } : undefined,
    });
    return response.data;
  },
  markNotificationsRead: async (id?: string): Promise<void> => {
    await api.patch('/portal/notifications/read', undefined, { params: id ? { id } : undefined });
  },

  // ── گفتگو با منتور ────────────────────────────────────────────────────────

  getMessages: async (
    before?: string,
  ): Promise<{ messages: PortalMessage[]; hasMore: boolean }> => {
    const response = await api.get('/portal/messages', {
      params: before ? { before } : undefined,
    });
    return response.data;
  },
  getUnreadMessageCount: async (): Promise<number> => {
    const response = await api.get('/portal/messages/unread-count');
    return response.data?.unread ?? 0;
  },
  sendMessage: async (data: { body?: string; attachmentOnly?: boolean }): Promise<PortalMessage> => {
    const response = await api.post('/portal/messages', data);
    return response.data;
  },
  attachToMessage: async (
    messageId: string,
    file: File,
    durationSec?: number,
  ): Promise<PortalMessageAttachment> => {
    const form = new FormData();
    form.append('file', file);
    if (durationSec !== undefined) form.append('durationSec', String(Math.round(durationSec)));
    const response = await api.post('/portal/messages/' + messageId + '/attachments', form);
    return response.data;
  },
  /**
   * پیوست را با احراز هویت می‌گیرد و object URL می‌سازد؛ لینک مستقیم کار
   * نمی‌کند چون مسیر پشت JWT است و مرورگر هدر Authorization نمی‌فرستد.
   */
  messageAttachmentUrl: async (fileId: string): Promise<string> => {
    const response = await api.get('/portal/messages/attachments/' + fileId, {
      responseType: 'blob',
    });
    return URL.createObjectURL(response.data as Blob);
  },
};

/** دانلود بلاب در مرورگر؛ URL موقت بعدش آزاد می‌شود */
function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
